import { useTranslation } from "react-i18next";
import { CreateHomeworkAssignmentRequestBody } from "@packages/api/madlenAPI.schemas";
import { format, differenceInMinutes } from "date-fns";
import { enUS, tr } from "date-fns/locale";
import { Text } from "../../../../../core";
import { CourseData } from "@packages/constant/CourseData";
import { useMemo } from "react";

const AssignmentOverviewForm = ({
  assignmentData,
  hideGeneralInfo,
}: {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  hideGeneralInfo?: boolean;
}) => {
  const { t, i18n } = useTranslation();

  const totalPoints = useMemo(() => {
    return assignmentData.questionTypes.reduce(
      (sum, qt) => sum + (qt.count ?? 0) * (qt.point ?? 0),
      0,
    );
  }, [assignmentData.questionTypes]);

  const formattedDuration = useMemo(() => {
    let durationInMinutes = 0;
    if (assignmentData.hwAssignmentType === "HOMEWORK") {
      if (assignmentData.endDate) {
        const startDate = assignmentData.startDate
          ? new Date(assignmentData.startDate)
          : new Date();
        durationInMinutes = differenceInMinutes(
          new Date(assignmentData.endDate),
          startDate,
        );
      } else {
        return "-";
      }
    } else {
      if (!assignmentData.duration) return "-";
      durationInMinutes = assignmentData.duration;
    }

    const diffMs = durationInMinutes * 60 * 1000;

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) {
      parts.push(`${days} ${t("common.time.days")}`);
    }
    if (hours > 0) {
      parts.push(`${hours} ${t("common.time.hours")}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} ${t("common.time.minutes")}`);
    }

    return parts.length > 0 ? parts.join(" ") : "0 " + t("common.time.minutes");
  }, [
    assignmentData.hwAssignmentType,
    assignmentData.duration,
    assignmentData.startDate,
    assignmentData.endDate,
    t,
  ]);

  return (
    <div className="space-y-2">
      {!hideGeneralInfo && (
        <div className="bg-white dark:bg-dark-500 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-3">
          <Text variant="h3" className="mb-2 text-lg">
            assignments.generator.properties.general
          </Text>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 break-words overflow-hidden">
              <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1 break-words">
                {t("assignments.generator.properties.name")}
              </div>
              <Text variant="h3" size="base" className="break-words">
                {assignmentData.name || "-"}
              </Text>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 break-words overflow-hidden">
              <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1 break-words">
                {t("assignments.generator.properties.course")}
              </div>
              <Text
                variant="h3"
                size="base"
                className="break-words overflow-wrap-anywhere"
              >
                {`courseType.${CourseData[assignmentData.courseType]?.name(
                  assignmentData.grade,
                )}`}
              </Text>
            </div>

            <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 break-words overflow-hidden">
              <div className="text-xs text-green-600 dark:text-green-400 font-medium mb-1 break-words">
                {t("assignments.generator.properties.grade")}
              </div>
              <Text
                variant="h3"
                size="base"
                className="break-words overflow-wrap-anywhere"
              >
                {assignmentData.grade}
              </Text>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-3 break-words overflow-hidden">
              <div className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1 break-words">
                {t("assignments.generator.properties.type")}
              </div>
              <Text
                variant="h3"
                size="base"
                className="break-words overflow-wrap-anywhere"
              >
                {assignmentData.hwAssignmentType === "HOMEWORK"
                  ? t("HOMEWORK")
                  : t("QUIZ")}
              </Text>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-dark-500 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-3">
        <Text variant="h3" size="lg" className="mb-2">
          assignments.generator.questions.distribution
        </Text>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {assignmentData.questionTypes.map((qt) => (
                <div
                  key={qt.questionType}
                  className="flex justify-between items-center bg-gray-50 dark:bg-dark-600 p-2.5 rounded-lg overflow-hidden"
                >
                  <div className="flex-1 min-w-0 break-words">
                    <Text
                      variant="span"
                      size="sm"
                      className="block break-words overflow-wrap-anywhere"
                    >
                      {`assignments.generator.questions.types.${qt.questionType}`}
                    </Text>
                    <Text
                      variant="span"
                      status="secondary"
                      size="xs"
                      className="break-words"
                    >
                      ({qt.count}{" "}
                      {t("assignments.generator.questions.count").toLowerCase()}
                      )
                    </Text>
                  </div>
                  <div className="text-blue-600 dark:text-blue-400 font-semibold text-sm ml-2 whitespace-nowrap flex-shrink-0">
                    {t("assignments.generator.preview.points", {
                      points: (qt.count ?? 0) * (qt.point ?? 0),
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 flex flex-col justify-center items-center break-words overflow-hidden">
            <div className="text-xs text-blue-600 dark:text-blue-400 mb-1 font-bold break-words text-center">
              {t("assignments.generator.questions.totalPoints")}: {totalPoints}
            </div>

            <div className="mt-2 flex items-center gap-1.5 text-sm break-words">
              <div className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-500 flex-shrink-0"></div>
              <Text
                variant="span"
                status="subtle"
                size="xs"
                className="break-words overflow-wrap-anywhere"
              >
                {t("assignments.generator.questions.duration")}:{" "}
                {formattedDuration}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {!hideGeneralInfo &&
        (assignmentData.startDate || assignmentData.endDate) && (
          <div className="bg-white dark:bg-dark-500 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-3">
            <Text variant="h3" className="mb-2" size="lg">
              assignments.generator.properties.schedule
            </Text>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {assignmentData.startDate && (
                <div className="bg-gray-50 dark:bg-dark-600 rounded-lg p-3 break-words overflow-hidden">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1 break-words">
                    {t("assignments.generator.properties.startDate")}
                  </div>
                  <Text
                    variant="h3"
                    size="base"
                    className="break-words overflow-wrap-anywhere"
                  >
                    {format(new Date(assignmentData.startDate), "PPpp", {
                      locale: i18n.language === "tr" ? tr : enUS,
                    })}
                  </Text>
                </div>
              )}

              {assignmentData.endDate && (
                <div className="bg-gray-50 dark:bg-dark-600 rounded-lg p-3 break-words overflow-hidden">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1 break-words">
                    {t("assignments.generator.properties.endDate")}
                  </div>
                  <Text
                    variant="h3"
                    size="base"
                    className="break-words overflow-wrap-anywhere"
                  >
                    {format(new Date(assignmentData.endDate), "PPpp", {
                      locale: i18n.language === "tr" ? tr : enUS,
                    })}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default AssignmentOverviewForm;
